import { PayloadAction, createSelector, createSlice } from '@reduxjs/toolkit';
import {
  AppWindow,
  ApplicationType,
  WindowGeometry,
  WindowMetaData,
} from '../types';
import {
  createWindow,
  findLastActiveWindow,
  findWindowIndex,
  removeWindow,
} from '../utils';
import { RootState } from '../../core/redux/store';
import {
  getMaxSizeAndPos,
  getTopAndBottomSectionSum,
} from '../../sections/utils';
import { IPosition2d } from '../../core/types/ui/position';
import { put, select } from 'redux-saga/effects';
import { SectionsState } from '../../sections/types';
import {
  adjustWindows,
  getAdjustedWindowGeometry,
} from '../helpers/adjustWindow';
import { useAppSelector } from '../../core/hooks/storeHooks';
import { isEqual } from 'lodash';

interface WindowsInitialState {
  windowList: AppWindow[];
  activeWindow: string;
}

const initialState: WindowsInitialState = {
  windowList: [],
  activeWindow: '',
};

const windowsSlice = createSlice({
  name: 'windows',
  initialState,
  reducers: {
    resetWindows: () => {
      return initialState;
    },
    addWindows: (
      state,
      action: PayloadAction<{
        windows: AppWindow[];
        activeWindowId: string | undefined;
      }>
    ) => {
      const { windows, activeWindowId } = action.payload;
      state.windowList = windows;
      if (activeWindowId) {
        const window = state.windowList.find(
          (window) => window.id === activeWindowId
        );
        state.activeWindow = window
          ? activeWindowId
          : windows[windows.length - 1]?.id ?? '';
      } else {
        state.activeWindow = windows[windows.length - 1]?.id ?? '';
      }
    },
    addNewWindow: (state, action: PayloadAction<AppWindow>) => {
      state.windowList.push(action.payload);
      state.activeWindow = action.payload.id; //state.windowList.length;
    },
    updateWindowGeometry: (
      state,
      action: PayloadAction<{ id: string; geometry: WindowGeometry }>
    ) => {
      state.windowList = state.windowList.map((window) => {
        if (window.id === action.payload.id) {
          return {
            ...window,
            ...action.payload.geometry,
          };
        }
        return window;
      });
    },
    updateWindowPosition: (
      state,
      action: PayloadAction<{ id: string; position: IPosition2d }>
    ) => {
      state.windowList = state.windowList.map((window) => {
        if (window.id === action.payload.id) {
          return {
            ...window,
            ...action.payload.position,
          };
        }
        return window;
      });
    },
    switchWindow: (state, action: PayloadAction<string>) => {
      if (state.activeWindow !== action.payload)
        state.activeWindow = action.payload;
    },
    switchActiveSheet: (
      state,
      action: PayloadAction<{ id: string; sheetId: string }>
    ) => {
      const windowIndex = findWindowIndex(state.windowList, action.payload.id);
      if (windowIndex !== -1) {
        state.activeWindow = action.payload.id;
        const window = state.windowList[windowIndex];
        if (window.metaData.appType === ApplicationType.Sheet) {
          window.metaData.sheetId = action.payload.sheetId;
          state.windowList[windowIndex] = window;
        }
      }
    },
    minimizeWindow: (state, action: PayloadAction<string>) => {
      const windowIndex = findWindowIndex(state.windowList, action.payload);
      if (windowIndex !== -1) {
        state.windowList[windowIndex].isMinimized = true;
        state.activeWindow = findLastActiveWindow(state.windowList)?.id || '';
      }
    },
    undoMinimizeWindow: (state, action: PayloadAction<string>) => {
      const windowIndex = findWindowIndex(state.windowList, action.payload);
      if (windowIndex !== -1) {
        state.windowList[windowIndex].isMinimized = false;
        state.activeWindow = state.windowList[windowIndex].id;
      }
    },
    maximizeWindow: (state, action: PayloadAction<string>) => {
      const windowIndex = findWindowIndex(state.windowList, action.payload);
      if (windowIndex !== -1) {
        state.windowList[windowIndex].isMaximized = true;
        state.activeWindow = state.windowList[windowIndex].id;
      }
    },
    undoMaximizeWindow: (state, action: PayloadAction<string>) => {
      const windowIndex = findWindowIndex(state.windowList, action.payload);
      if (windowIndex !== -1) {
        state.windowList[windowIndex].isMaximized = false;
        state.activeWindow = state.windowList[windowIndex].id;
      }
    },
    closeWindow: (state, action: PayloadAction<string>) => {
      state.windowList = removeWindow(state.windowList, action.payload);
      state.activeWindow = findLastActiveWindow(state.windowList)?.id || '';
    },
  },
});

export const {
  addWindows,
  resetWindows,

  addNewWindow,

  updateWindowGeometry,
  updateWindowPosition,

  switchWindow,
  switchActiveSheet,

  minimizeWindow,
  undoMinimizeWindow,

  maximizeWindow,
  undoMaximizeWindow,

  closeWindow,
} = windowsSlice.actions;
export default windowsSlice.reducer;

// Add Window Saga
export function* startAddNewWindow(windowMetaData: WindowMetaData) {
  let window: AppWindow | null = null;

  if (windowMetaData.appType === ApplicationType.Sheet) {
    window = yield select((state) =>
      findWindowByGroupId(windowMetaData.groupId)(state)
    );
  }

  if (window && windowMetaData.appType === ApplicationType.Sheet) {
    yield put(
      switchActiveSheet({
        id: window.id,
        sheetId: windowMetaData.sheetId,
      })
    );
  } else {
    const sections: SectionsState = yield select((state) => state.sections);
    const maxSize = getMaxSizeAndPos(sections);
    window = createWindow(maxSize, windowMetaData);
    yield put(addNewWindow(window));
  }
}

// Selectors
// Selector to get the non-minimized window IDs
export const selectWindowsInViewport = createSelector(
  (state: RootState) => state.windows.windowList,
  (windowList) =>
    windowList
      .filter((window) => !window.isMinimized)
      .map((window) => window.id)
);

export const selectWindowById = (windowId: string) =>
  createSelector(
    (state: RootState) => state.windows.windowList,
    (windowList) => windowList.find((window) => window.id === windowId) || null
  );

export const selectActiveWindow = createSelector(
  (state: RootState) =>
    state.windows.windowList.find(
      (window) => window.id === state.windows.activeWindow
    ),
  (window) => window
);

// export const findWindowByGroupId = (groupId: string) =>
//   createSelector(
//     (state: RootState) => state.windows.windowList,
//     (windowList) =>
//       windowList.find(
//         (window) =>
//           window.metaData.appType === ApplicationType.Sheet &&
//           window.metaData.groupId === groupId
//       ) || null
//   );

// export const findWindowBySheetId = (sheetId: string) =>
//   createSelector(
//     (state: RootState) => state.windows.windowList,
//     (windowList) =>
//       windowList.find(
//         (window) =>
//           window.metaData.appType === ApplicationType.Sheet &&
//           window.metaData.sheetId === sheetId
//       ) || null
//   );

const selectWindowData = (state: RootState) => {
  const { topSectionSum, bottomSectionSum } = getTopAndBottomSectionSum(
    state.sections
  );
  const maxDims = getMaxSizeAndPos(state.sections);
  return {
    windowList: state.windows.windowList,
    maxDims,
    topSectionSum,
    bottomSectionSum,
  };
};

export const createFindWindowSelector =
  (property: 'groupId' | 'sheetId') => (targetId: string) =>
    createSelector(
      selectWindowData,
      ({ windowList, maxDims, topSectionSum, bottomSectionSum }) => {
        const window = windowList.find(
          (window) =>
            window.metaData.appType === ApplicationType.Sheet &&
            window.metaData[property] === targetId
        );

        if (window) {
          const adjustedGeometry = getAdjustedWindowGeometry(
            window,
            topSectionSum,
            bottomSectionSum,
            maxDims
          );

          return {
            ...window,
            ...adjustedGeometry,
          };
        }

        return null;
      }
    );

export const findWindowByGroupId = createFindWindowSelector('groupId');
export const findWindowBySheetId = createFindWindowSelector('sheetId');
